import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, makeEnvironmentProviders, NgModule, InjectionToken } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Subject, of, from, race, throwError, combineLatest, merge } from 'rxjs';
import { filter, tap, debounceTime, delay, switchMap, map, first, catchError, timeout, take, mergeMap } from 'rxjs/operators';

/**
 * A validation handler that isn't validating nothing.
 * Can be used to skip validation (at your own risk).
 */
class NullValidationHandler {
  validateSignature(validationParams) {
    return Promise.resolve(null);
  }
  validateAtHash(validationParams) {
    return Promise.resolve(true);
  }
}
class OAuthModuleConfig {}
class OAuthResourceServerConfig {}
class DateTimeProvider {}
let SystemDateTimeProvider = /*#__PURE__*/(() => {
  class SystemDateTimeProvider extends DateTimeProvider {
    now() {
      return Date.now();
    }
    new() {
      return new Date();
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵSystemDateTimeProvider_BaseFactory;
        return function SystemDateTimeProvider_Factory(__ngFactoryType__) {
          return (ɵSystemDateTimeProvider_BaseFactory || (ɵSystemDateTimeProvider_BaseFactory = i0.ɵɵgetInheritedFactory(SystemDateTimeProvider)))(__ngFactoryType__ || SystemDateTimeProvider);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SystemDateTimeProvider,
        factory: SystemDateTimeProvider.ɵfac
      });
    }
  }
  return SystemDateTimeProvider;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Additional options that can be passed to tryLogin.
 */
class LoginOptions {
  constructor() {
    /**
     * Set this to true to disable the nonce
     * check which is used to avoid
     * replay attacks.
     * This flag should never be true in
     * production environments.
     */
    this.disableNonceCheck = false;
    /**
     * Normally, you want to clear your hash fragment after
     * the lib read the token(s) so that they are not displayed
     * anymore in the url. If not, set this to true. For code flow
     * this controls removing query string values.
     */
    this.preventClearHashAfterLogin = false;
  }
}
/**
 * Defines the logging interface the OAuthService uses
 * internally. Is compatible with the `console` object,
 * but you can provide your own implementation as well
 * through dependency injection.
 */
class OAuthLogger {}
/**
 * Defines a simple storage that can be used for
 * storing the tokens at client side.
 * Is compatible to localStorage and sessionStorage,
 * but you can also create your own implementations.
 */
class OAuthStorage {}
let MemoryStorage = /*#__PURE__*/(() => {
  class MemoryStorage {
    constructor() {
      this.data = new Map();
    }
    getItem(key) {
      return this.data.get(key);
    }
    removeItem(key) {
      this.data.delete(key);
    }
    setItem(key, data) {
      this.data.set(key, data);
    }
    static {
      this.ɵfac = function MemoryStorage_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MemoryStorage)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MemoryStorage,
        factory: MemoryStorage.ɵfac
      });
    }
  }
  return MemoryStorage;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * Represents the received tokens, the received state
 * and the parsed claims from the id-token.
 */
class ReceivedTokens {}
class OAuthEvent {
  constructor(type) {
    this.type = type;
  }
}
class OAuthSuccessEvent extends OAuthEvent {
  constructor(type, info = null) {
    super(type);
    this.info = info;
  }
}
class OAuthInfoEvent extends OAuthEvent {
  constructor(type, info = null) {
    super(type);
    this.info = info;
  }
}
class OAuthErrorEvent extends OAuthEvent {
  constructor(type, reason, params = null) {
    super(type);
    this.reason = reason;
    this.params = params;
  }
}

// see: https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#The_.22Unicode_Problem.22
function b64DecodeUnicode(str) {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
}
function base64UrlEncode(str) {
  const base64 = btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
class AuthConfig {
  constructor(json) {
    /**
     * The client's id as registered with the auth server
     */
    this.clientId = '';
    /**
     * The client's redirectUri as registered with the auth server
     */
    this.redirectUri = '';
    /**
     * An optional second redirectUri where the auth server
     * redirects the user to after logging out.
     */
    this.postLogoutRedirectUri = '';
    /**
     * Defines whether to use 'redirectUri' as a replacement
     * of 'postLogoutRedirectUri' if the latter is not set.
     */
    this.redirectUriAsPostLogoutRedirectUriFallback = true;
    /**
     * The auth server's endpoint that allows to log
     * the user in when using implicit flow.
     */
    this.loginUrl = '';
    /**
     * The requested scopes
     */
    this.scope = 'openid profile';
    this.resource = '';
    this.rngUrl = '';
    /**
     * Defines whether to use OpenId Connect during
     * implicit flow.
     */
    this.oidc = true;
    /**
     * Defines whether to request an access token during
     * implicit flow.
     */
    this.requestAccessToken = true;
    this.options = null;
    /**
     * The issuer's uri.
     */
    this.issuer = '';
    /**
     * The logout url.
     */
    this.logoutUrl = '';
    /**
     * Defines whether to clear the hash fragment after logging in.
     */
    this.clearHashAfterLogin = true;
    /**
     * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
     */
    this.tokenEndpoint = null;
    /**
     * Url of the revocation endpoint as defined by OpenId Connect and OAuth 2.
     */
    this.revocationEndpoint = null;
    /**
     * Names of known parameters sent out in the TokenResponse. https://tools.ietf.org/html/rfc6749#section-5.1
     */
    this.customTokenParameters = [];
    /**
     * Url of the userinfo endpoint as defined by OpenId Connect.
     */
    this.userinfoEndpoint = null;
    this.responseType = '';
    /**
     * Defines whether additional debug information should
     * be shown at the console. Note that in certain browsers
     * the verbosity of the console needs to be explicitly set
     * to include Debug level messages.
     */
    this.showDebugInformation = false;
    /**
     * The redirect uri used when doing silent refresh.
     */
    this.silentRefreshRedirectUri = '';
    this.silentRefreshMessagePrefix = '';
    /**
     * Set this to true to display the iframe used for
     * silent refresh for debugging.
     */
    this.silentRefreshShowIFrame = false;
    /**
     * Timeout for silent refresh.
     * @internal
     * @deprecated use silentRefreshTimeout
     */
    this.siletRefreshTimeout = 1000 * 20;
    /**
     * Timeout for silent refresh.
     */
    this.silentRefreshTimeout = 1000 * 20;
    /**
     * Some auth servers don't allow using password flow
     * w/o a client secret while the standards do not
     * demand for it. In this case, you can set a password
     * here. As this password is exposed to the public
     * it does not bring additional security and is therefore
     * as good as using no password.
     */
    this.dummyClientSecret = '';
    /**
     * Defines whether https is required.
     * The default value is remoteOnly which only allows
     * http for localhost, while every other domains need
     * to be used with https.
     */
    this.requireHttps = 'remoteOnly';
    /**
     * Defines whether every url provided by the discovery
     * document has to start with the issuer's url.
     */
    this.strictDiscoveryDocumentValidation = true;
    /**
     * JSON Web Key Set (https://tools.ietf.org/html/rfc7517)
     * with keys used to validate received id_tokens.
     * This is taken out of the disovery document. Can be set manually too.
     */
    this.jwks = null;
    /**
     * Map with additional query parameter that are appended to
     * the request when initializing implicit flow.
     */
    this.customQueryParams = null;
    this.silentRefreshIFrameName = 'angular-oauth-oidc-silent-refresh-iframe';
    /**
     * Defines when the token_timeout event should be raised.
     * If you set this to the default value 0.75, the event
     * is triggered after 75% of the token's life time.
     */
    this.timeoutFactor = 0.75;
    /**
     * If true, the lib will try to check whether the user
     * is still logged in on a regular basis as described
     * in http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
     */
    this.sessionChecksEnabled = false;
    /**
     * Interval in msec for checking the session
     * according to http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
     */
    this.sessionCheckIntervall = 3 * 1000;
    /**
     * Url for the iframe used for session checks
     */
    this.sessionCheckIFrameUrl = null;
    /**
     * Name of the iframe to use for session checks
     */
    this.sessionCheckIFrameName = 'angular-oauth-oidc-check-session-iframe';
    /**
     * This property has been introduced to disable at_hash checks
     * and is indented for Identity Provider that does not deliver
     * an at_hash EVEN THOUGH its recommended by the OIDC specs.
     * Of course, when disabling these checks then we are bypassing
     * a security check which means we are more vulnerable.
     */
    this.disableAtHashCheck = false;
    /**
     * Defines wether to check the subject of a refreshed token after silent refresh.
     * Normally, it should be the same as before.
     */
    this.skipSubjectCheck = false;
    this.useIdTokenHintForSilentRefresh = false;
    /**
     * Defined whether to skip the validation of the issuer in the discovery document.
     * Normally, the discovey document's url starts with the url of the issuer.
     */
    this.skipIssuerCheck = false;
    /**
     * final state sent to issuer is built as follows:
     * state = nonce + nonceStateSeparator + additional state
     * Default separator is ';' (encoded %3B).
     * In rare cases, this character might be forbidden or inconvenient to use by the issuer so it can be customized.
     */
    this.nonceStateSeparator = ';';
    /**
     * Set this to true to use HTTP BASIC auth for AJAX calls
     */
    this.useHttpBasicAuth = false;
    /**
     * Decreases the Expiration time of tokens by this number of seconds
     */
    this.decreaseExpirationBySec = 0;
    /**
     * The interceptors waits this time span if there is no token
     */
    this.waitForTokenInMsec = 0;
    /**
     * Code Flow is by defauld used together with PKCI which is also higly recommented.
     * You can disbale it here by setting this flag to true.
     * https://tools.ietf.org/html/rfc7636#section-1.1
     */
    this.disablePKCE = false;
    /**
     * Set this to true to preserve the requested route including query parameters after code flow login.
     * This setting enables deep linking for the code flow.
     */
    this.preserveRequestedRoute = false;
    /**
     * Allows to disable the timer for the id_token used
     * for token refresh
     */
    this.disableIdTokenTimer = false;
    /**
     * Blocks other origins requesting a silent refresh
     */
    this.checkOrigin = false;
    /**
     * This property allows you to override the method that is used to open the login url,
     * allowing a way for implementations to specify their own method of routing to new
     * urls.
     */
    this.openUri = uri => {
      location.href = uri;
    };
    if (json) {
      Object.assign(this, json);
    }
  }
}

/**
 * This custom encoder allows charactes like +, % and / to be used in passwords
 */
class WebHttpUrlEncodingCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}

/**
 * Interface for Handlers that are hooked in to
 * validate tokens.
 */
class ValidationHandler {}
/**
 * This abstract implementation of ValidationHandler already implements
 * the method validateAtHash. However, to make use of it,
 * you have to override the method calcHash.
 */
class AbstractValidationHandler {
  /**
   * Validates the at_hash in an id_token against the received access_token.
   */
  async validateAtHash(params) {
    const hashAlg = this.inferHashAlgorithm(params.idTokenHeader);
    const tokenHash = await this.calcHash(params.accessToken, hashAlg); // sha256(accessToken, { asString: true });
    const leftMostHalf = tokenHash.substr(0, tokenHash.length / 2);
    const atHash = base64UrlEncode(leftMostHalf);
    const claimsAtHash = params.idTokenClaims['at_hash'].replace(/=/g, '');
    if (atHash !== claimsAtHash) {
      console.error('exptected at_hash: ' + atHash);
      console.error('actual at_hash: ' + claimsAtHash);
    }
    return atHash === claimsAtHash;
  }
  /**
   * Infers the name of the hash algorithm to use
   * from the alg field of an id_token.
   *
   * @param jwtHeader the id_token's parsed header
   */
  inferHashAlgorithm(jwtHeader) {
    const alg = jwtHeader['alg'];
    if (!alg.match(/^.S[0-9]{3}$/)) {
      throw new Error('Algorithm not supported: ' + alg);
    }
    return 'sha-' + alg.substr(2);
  }
}
let UrlHelperService = /*#__PURE__*/(() => {
  class UrlHelperService {
    getHashFragmentParams(customHashFragment) {
      let hash = customHashFragment || window.location.hash;
      hash = decodeURIComponent(hash);
      if (hash.indexOf('#') !== 0) {
        return {};
      }
      const questionMarkPosition = hash.indexOf('?');
      if (questionMarkPosition > -1) {
        hash = hash.substr(questionMarkPosition + 1);
      } else {
        hash = hash.substr(1);
      }
      return this.parseQueryString(hash);
    }
    parseQueryString(queryString) {
      const data = {};
      let pair, separatorIndex, escapedKey, escapedValue, key, value;
      if (queryString === null) {
        return data;
      }
      const pairs = queryString.split('&');
      for (let i = 0; i < pairs.length; i++) {
        pair = pairs[i];
        separatorIndex = pair.indexOf('=');
        if (separatorIndex === -1) {
          escapedKey = pair;
          escapedValue = null;
        } else {
          escapedKey = pair.substr(0, separatorIndex);
          escapedValue = pair.substr(separatorIndex + 1);
        }
        key = decodeURIComponent(escapedKey);
        value = decodeURIComponent(escapedValue);
        if (key.substr(0, 1) === '/') {
          key = key.substr(1);
        }
        data[key] = value;
      }
      return data;
    }
    static {
      this.ɵfac = function UrlHelperService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UrlHelperService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UrlHelperService,
        factory: UrlHelperService.ɵfac
      });
    }
  }
  return UrlHelperService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Credits: https://github.com/dchest/fast-sha256-js/tree/master/src
// We add this lib directly b/c the published version of fast-sha256-js
// is commonjs and hence leads to a warning about tree-shakability emitted
// by the Angular CLI
// SHA-256 (+ HMAC and PBKDF2) for JavaScript.
//
// Written in 2014-2016 by Dmitry Chestnykh.
// Public domain, no warranty.
//
// Functions (accept and return Uint8Arrays):
//
//   sha256(message) -> hash
//   sha256.hmac(key, message) -> mac
//   sha256.pbkdf2(password, salt, rounds, dkLen) -> dk
//
//  Classes:
//
//   new sha256.Hash()
//   new sha256.HMAC(key)
//
const digestLength = 32;
const blockSize = 64;
// SHA-256 constants
const K = new Uint32Array([0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2]);
function hashBlocks(w, v, p, pos, len) {
  let a, b, c, d, e, f, g, h, u, i, j, t1, t2;
  while (len >= 64) {
    a = v[0];
    b = v[1];
    c = v[2];
    d = v[3];
    e = v[4];
    f = v[5];
    g = v[6];
    h = v[7];
    for (i = 0; i < 16; i++) {
      j = pos + i * 4;
      w[i] = (p[j] & 0xff) << 24 | (p[j + 1] & 0xff) << 16 | (p[j + 2] & 0xff) << 8 | p[j + 3] & 0xff;
    }
    for (i = 16; i < 64; i++) {
      u = w[i - 2];
      t1 = (u >>> 17 | u << 32 - 17) ^ (u >>> 19 | u << 32 - 19) ^ u >>> 10;
      u = w[i - 15];
      t2 = (u >>> 7 | u << 32 - 7) ^ (u >>> 18 | u << 32 - 18) ^ u >>> 3;
      w[i] = (t1 + w[i - 7] | 0) + (t2 + w[i - 16] | 0);
    }
    for (i = 0; i < 64; i++) {
      t1 = (((e >>> 6 | e << 32 - 6) ^ (e >>> 11 | e << 32 - 11) ^ (e >>> 25 | e << 32 - 25)) + (e & f ^ ~e & g) | 0) + (h + (K[i] + w[i] | 0) | 0) | 0;
      t2 = ((a >>> 2 | a << 32 - 2) ^ (a >>> 13 | a << 32 - 13) ^ (a >>> 22 | a << 32 - 22)) + (a & b ^ a & c ^ b & c) | 0;
      h = g;
      g = f;
      f = e;
      e = d + t1 | 0;
      d = c;
      c = b;
      b = a;
      a = t1 + t2 | 0;
    }
    v[0] += a;
    v[1] += b;
    v[2] += c;
    v[3] += d;
    v[4] += e;
    v[5] += f;
    v[6] += g;
    v[7] += h;
    pos += 64;
    len -= 64;
  }
  return pos;
}
// Hash implements SHA256 hash algorithm.
class Hash {
  constructor() {
    this.digestLength = digestLength;
    this.blockSize = blockSize;
    // Note: Int32Array is used instead of Uint32Array for performance reasons.
    this.state = new Int32Array(8); // hash state
    this.temp = new Int32Array(64); // temporary state
    this.buffer = new Uint8Array(128); // buffer for data to hash
    this.bufferLength = 0; // number of bytes in buffer
    this.bytesHashed = 0; // number of total bytes hashed
    this.finished = false; // indicates whether the hash was finalized
    this.reset();
  }
  // Resets hash state making it possible
  // to re-use this instance to hash other data.
  reset() {
    this.state[0] = 0x6a09e667;
    this.state[1] = 0xbb67ae85;
    this.state[2] = 0x3c6ef372;
    this.state[3] = 0xa54ff53a;
    this.state[4] = 0x510e527f;
    this.state[5] = 0x9b05688c;
    this.state[6] = 0x1f83d9ab;
    this.state[7] = 0x5be0cd19;
    this.bufferLength = 0;
    this.bytesHashed = 0;
    this.finished = false;
    return this;
  }
  // Cleans internal buffers and re-initializes hash state.
  clean() {
    for (let i = 0; i < this.buffer.length; i++) {
      this.buffer[i] = 0;
    }
    for (let i = 0; i < this.temp.length; i++) {
      this.temp[i] = 0;
    }
    this.reset();
  }
  // Updates hash state with the given data.
  //
  // Optionally, length of the data can be specified to hash
  // fewer bytes than data.length.
  //
  // Throws error when trying to update already finalized hash:
  // instance must be reset to use it again.
  update(data, dataLength = data.length) {
    if (this.finished) {
      throw new Error("SHA256: can't update because hash was finished.");
    }
    let dataPos = 0;
    this.bytesHashed += dataLength;
    if (this.bufferLength > 0) {
      while (this.bufferLength < 64 && dataLength > 0) {
        this.buffer[this.bufferLength++] = data[dataPos++];
        dataLength--;
      }
      if (this.bufferLength === 64) {
        hashBlocks(this.temp, this.state, this.buffer, 0, 64);
        this.bufferLength = 0;
      }
    }
    if (dataLength >= 64) {
      dataPos = hashBlocks(this.temp, this.state, data, dataPos, dataLength);
      dataLength %= 64;
    }
    while (dataLength > 0) {
      this.buffer[this.bufferLength++] = data[dataPos++];
      dataLength--;
    }
    return this;
  }
  // Finalizes hash state and puts hash into out.
  //
  // If hash was already finalized, puts the same value.
  finish(out) {
    if (!this.finished) {
      const bytesHashed = this.bytesHashed;
      const left = this.bufferLength;
      const bitLenHi = bytesHashed / 0x20000000 | 0;
      const bitLenLo = bytesHashed << 3;
      const padLength = bytesHashed % 64 < 56 ? 64 : 128;
      this.buffer[left] = 0x80;
      for (let i = left + 1; i < padLength - 8; i++) {
        this.buffer[i] = 0;
      }
      this.buffer[padLength - 8] = bitLenHi >>> 24 & 0xff;
      this.buffer[padLength - 7] = bitLenHi >>> 16 & 0xff;
      this.buffer[padLength - 6] = bitLenHi >>> 8 & 0xff;
      this.buffer[padLength - 5] = bitLenHi >>> 0 & 0xff;
      this.buffer[padLength - 4] = bitLenLo >>> 24 & 0xff;
      this.buffer[padLength - 3] = bitLenLo >>> 16 & 0xff;
      this.buffer[padLength - 2] = bitLenLo >>> 8 & 0xff;
      this.buffer[padLength - 1] = bitLenLo >>> 0 & 0xff;
      hashBlocks(this.temp, this.state, this.buffer, 0, padLength);
      this.finished = true;
    }
    for (let i = 0; i < 8; i++) {
      out[i * 4 + 0] = this.state[i] >>> 24 & 0xff;
      out[i * 4 + 1] = this.state[i] >>> 16 & 0xff;
      out[i * 4 + 2] = this.state[i] >>> 8 & 0xff;
      out[i * 4 + 3] = this.state[i] >>> 0 & 0xff;
    }
    return this;
  }
  // Returns the final hash digest.
  digest() {
    const out = new Uint8Array(this.digestLength);
    this.finish(out);
    return out;
  }
  // Internal function for use in HMAC for optimization.
  _saveState(out) {
    for (let i = 0; i < this.state.length; i++) {
      out[i] = this.state[i];
    }
  }
  // Internal function for use in HMAC for optimization.
  _restoreState(from, bytesHashed) {
    for (let i = 0; i < this.state.length; i++) {
      this.state[i] = from[i];
    }
    this.bytesHashed = bytesHashed;
    this.finished = false;
    this.bufferLength = 0;
  }
}
// HMAC implements HMAC-SHA256 message authentication algorithm.
class HMAC {
  constructor(key) {
    this.inner = new Hash();
    this.outer = new Hash();
    this.blockSize = this.inner.blockSize;
    this.digestLength = this.inner.digestLength;
    const pad = new Uint8Array(this.blockSize);
    if (key.length > this.blockSize) {
      new Hash().update(key).finish(pad).clean();
    } else {
      for (let i = 0; i < key.length; i++) {
        pad[i] = key[i];
      }
    }
    for (let i = 0; i < pad.length; i++) {
      pad[i] ^= 0x36;
    }
    this.inner.update(pad);
    for (let i = 0; i < pad.length; i++) {
      pad[i] ^= 0x36 ^ 0x5c;
    }
    this.outer.update(pad);
    this.istate = new Uint32Array(8);
    this.ostate = new Uint32Array(8);
    this.inner._saveState(this.istate);
    this.outer._saveState(this.ostate);
    for (let i = 0; i < pad.length; i++) {
      pad[i] = 0;
    }
  }
  // Returns HMAC state to the state initialized with key
  // to make it possible to run HMAC over the other data with the same
  // key without creating a new instance.
  reset() {
    this.inner._restoreState(this.istate, this.inner.blockSize);
    this.outer._restoreState(this.ostate, this.outer.blockSize);
    return this;
  }
  // Cleans HMAC state.
  clean() {
    for (let i = 0; i < this.istate.length; i++) {
      this.ostate[i] = this.istate[i] = 0;
    }
    this.inner.clean();
    this.outer.clean();
  }
  // Updates state with provided data.
  update(data) {
    this.inner.update(data);
    return this;
  }
  // Finalizes HMAC and puts the result in out.
  finish(out) {
    if (this.outer.finished) {
      this.outer.finish(out);
    } else {
      this.inner.finish(out);
      this.outer.update(out, this.digestLength).finish(out);
    }
    return this;
  }
  // Returns message authentication code.
  digest() {
    const out = new Uint8Array(this.digestLength);
    this.finish(out);
    return out;
  }
}
// Returns SHA256 hash of data.
function hash(data) {
  const h = new Hash().update(data);
  const digest = h.digest();
  h.clean();
  return digest;
}
// Returns HMAC-SHA256 of data under the key.
function hmac(key, data) {
  const h = new HMAC(key).update(data);
  const digest = h.digest();
  h.clean();
  return digest;
}
// Fills hkdf buffer like this:
// T(1) = HMAC-Hash(PRK, T(0) | info | 0x01)
function fillBuffer(buffer, hmac, info, counter) {
  // Counter is a byte value: check if it overflowed.
  const num = counter[0];
  if (num === 0) {
    throw new Error('hkdf: cannot expand more');
  }
  // Prepare HMAC instance for new data with old key.
  hmac.reset();
  // Hash in previous output if it was generated
  // (i.e. counter is greater than 1).
  if (num > 1) {
    hmac.update(buffer);
  }
  // Hash in info if it exists.
  if (info) {
    hmac.update(info);
  }
  // Hash in the counter.
  hmac.update(counter);
  // Output result to buffer and clean HMAC instance.
  hmac.finish(buffer);
  // Increment counter inside typed array, this works properly.
  counter[0]++;
}
const hkdfSalt = new Uint8Array(digestLength); // Filled with zeroes.
function hkdf(key, salt = hkdfSalt, info, length = 32) {
  const counter = new Uint8Array([1]);
  // HKDF-Extract uses salt as HMAC key, and key as data.
  const okm = hmac(salt, key);
  // Initialize HMAC for expanding with extracted key.
  // Ensure no collisions with `hmac` function.
  const hmac_ = new HMAC(okm);
  // Allocate buffer.
  const buffer = new Uint8Array(hmac_.digestLength);
  let bufpos = buffer.length;
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    if (bufpos === buffer.length) {
      fillBuffer(buffer, hmac_, info, counter);
      bufpos = 0;
    }
    out[i] = buffer[bufpos++];
  }
  hmac_.clean();
  buffer.fill(0);
  counter.fill(0);
  return out;
}
// Derives a key from password and salt using PBKDF2-HMAC-SHA256
// with the given number of iterations.
//
// The number of bytes returned is equal to dkLen.
//
// (For better security, avoid dkLen greater than hash length - 32 bytes).
function pbkdf2(password, salt, iterations, dkLen) {
  const prf = new HMAC(password);
  const len = prf.digestLength;
  const ctr = new Uint8Array(4);
  const t = new Uint8Array(len);
  const u = new Uint8Array(len);
  const dk = new Uint8Array(dkLen);
  for (let i = 0; i * len < dkLen; i++) {
    const c = i + 1;
    ctr[0] = c >>> 24 & 0xff;
    ctr[1] = c >>> 16 & 0xff;
    ctr[2] = c >>> 8 & 0xff;
    ctr[3] = c >>> 0 & 0xff;
    prf.reset();
    prf.update(salt);
    prf.update(ctr);
    prf.finish(u);
    for (let j = 0; j < len; j++) {
      t[j] = u[j];
    }
    for (let j = 2; j <= iterations; j++) {
      prf.reset();
      prf.update(u).finish(u);
      for (let k = 0; k < len; k++) {
        t[k] ^= u[k];
      }
    }
    for (let j = 0; j < len && i * len + j < dkLen; j++) {
      dk[i * len + j] = t[j];
    }
  }
  for (let i = 0; i < len; i++) {
    t[i] = u[i] = 0;
  }
  for (let i = 0; i < 4; i++) {
    ctr[i] = 0;
  }
  prf.clean();
  return dk;
}

/**
 * Abstraction for crypto algorithms
 */
class HashHandler {}
function decodeUTF8(s) {
  if (typeof s !== 'string') throw new TypeError('expected string');
  const d = s,
    b = new Uint8Array(d.length);
  for (let i = 0; i < d.length; i++) b[i] = d.charCodeAt(i);
  return b;
}
function encodeUTF8(arr) {
  const s = [];
  for (let i = 0; i < arr.length; i++) s.push(String.fromCharCode(arr[i]));
  return s.join('');
}
let DefaultHashHandler = /*#__PURE__*/(() => {
  class DefaultHashHandler {
    async calcHash(valueToHash, algorithm) {
      // const encoder = new TextEncoder();
      // const hashArray = await window.crypto.subtle.digest(algorithm, data);
      // const data = encoder.encode(valueToHash);
      // const fhash = fsha256(valueToHash);
      const candHash = encodeUTF8(hash(decodeUTF8(valueToHash)));
      // const hashArray = (sha256 as any).array(valueToHash);
      // // const hashString = this.toHashString(hashArray);
      // const hashString = this.toHashString2(hashArray);
      // console.debug('hash orig - cand', candHash, hashString);
      // alert(1);
      return candHash;
    }
    toHashString2(byteArray) {
      let result = '';
      for (const e of byteArray) {
        result += String.fromCharCode(e);
      }
      return result;
    }
    toHashString(buffer) {
      const byteArray = new Uint8Array(buffer);
      let result = '';
      for (const e of byteArray) {
        result += String.fromCharCode(e);
      }
      return result;
    }
    static {
      this.ɵfac = function DefaultHashHandler_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DefaultHashHandler)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DefaultHashHandler,
        factory: DefaultHashHandler.ɵfac
      });
    }
  }
  return DefaultHashHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Service for logging in and logging out with
 * OIDC and OAuth2. Supports implicit flow and
 * password flow.
 */
let OAuthService = /*#__PURE__*/(() => {
  class OAuthService extends AuthConfig {
    constructor(ngZone, http, storage, tokenValidationHandler, config, urlHelper, logger, crypto, document, dateTimeService) {
      super();
      this.ngZone = ngZone;
      this.http = http;
      this.config = config;
      this.urlHelper = urlHelper;
      this.logger = logger;
      this.crypto = crypto;
      this.dateTimeService = dateTimeService;
      /**
       * @internal
       * Deprecated:  use property events instead
       */
      this.discoveryDocumentLoaded = false;
      /**
       * The received (passed around) state, when logging
       * in with implicit flow.
       */
      this.state = '';
      this.eventsSubject = new Subject();
      this.discoveryDocumentLoadedSubject = new Subject();
      this.grantTypesSupported = [];
      this.inImplicitFlow = false;
      this.saveNoncesInLocalStorage = false;
      this.debug('angular-oauth2-oidc v10');
      // See https://github.com/manfredsteyer/angular-oauth2-oidc/issues/773 for why this is needed
      this.document = document;
      if (!config) {
        config = {};
      }
      this.discoveryDocumentLoaded$ = this.discoveryDocumentLoadedSubject.asObservable();
      this.events = this.eventsSubject.asObservable();
      if (tokenValidationHandler) {
        this.tokenValidationHandler = tokenValidationHandler;
      }
      if (config) {
        this.configure(config);
      }
      try {
        if (storage) {
          this.setStorage(storage);
        } else if (typeof sessionStorage !== 'undefined') {
          this.setStorage(sessionStorage);
        }
      } catch (e) {
        console.error('No OAuthStorage provided and cannot access default (sessionStorage).' + 'Consider providing a custom OAuthStorage implementation in your module.', e);
      }
      // in IE, sessionStorage does not always survive a redirect
      if (this.checkLocalStorageAccessable()) {
        const ua = window?.navigator?.userAgent;
        const msie = ua?.includes('MSIE ') || ua?.includes('Trident');
        if (msie) {
          this.saveNoncesInLocalStorage = true;
        }
      }
      this.setupRefreshTimer();
    }
    checkLocalStorageAccessable() {
      if (typeof window === 'undefined') return false;
      const test = 'test';
      try {
        if (typeof window['localStorage'] === 'undefined') return false;
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    }
    /**
     * Use this method to configure the service
     * @param config the configuration
     */
    configure(config) {
      // For the sake of downward compatibility with
      // original configuration API
      Object.assign(this, new AuthConfig(), config);
      this.config = Object.assign({}, new AuthConfig(), config);
      if (this.sessionChecksEnabled) {
        this.setupSessionCheck();
      }
      this.configChanged();
    }
    configChanged() {
      this.setupRefreshTimer();
    }
    restartSessionChecksIfStillLoggedIn() {
      if (this.hasValidIdToken()) {
        this.initSessionCheck();
      }
    }
    restartRefreshTimerIfStillLoggedIn() {
      this.setupExpirationTimers();
    }
    setupSessionCheck() {
      this.events.pipe(filter(e => e.type === 'token_received')).subscribe(() => {
        this.initSessionCheck();
      });
    }
    /**
     * Will setup up silent refreshing for when the token is
     * about to expire. When the user is logged out via this.logOut method, the
     * silent refreshing will pause and not refresh the tokens until the user is
     * logged back in via receiving a new token.
     * @param params Additional parameter to pass
     * @param listenTo Setup automatic refresh of a specific token type
     */
    setupAutomaticSilentRefresh(params = {}, listenTo, noPrompt = true) {
      let shouldRunSilentRefresh = true;
      this.clearAutomaticRefreshTimer();
      this.automaticRefreshSubscription = this.events.pipe(tap(e => {
        if (e.type === 'token_received') {
          shouldRunSilentRefresh = true;
        } else if (e.type === 'logout') {
          shouldRunSilentRefresh = false;
        }
      }), filter(e => e.type === 'token_expires' && (listenTo == null || listenTo === 'any' || e.info === listenTo)), debounceTime(1000)).subscribe(() => {
        if (shouldRunSilentRefresh) {
          // this.silentRefresh(params, noPrompt).catch(_ => {
          this.refreshInternal(params, noPrompt).catch(() => {
            this.debug('Automatic silent refresh did not work');
          });
        }
      });
      this.restartRefreshTimerIfStillLoggedIn();
    }
    refreshInternal(params, noPrompt) {
      if (!this.useSilentRefresh && this.responseType === 'code') {
        return this.refreshToken();
      } else {
        return this.silentRefresh(params, noPrompt);
      }
    }
    /**
     * Convenience method that first calls `loadDiscoveryDocument(...)` and
     * directly chains using the `then(...)` part of the promise to call
     * the `tryLogin(...)` method.
     *
     * @param options LoginOptions to pass through to `tryLogin(...)`
     */
    loadDiscoveryDocumentAndTryLogin(options = null) {
      return this.loadDiscoveryDocument().then(() => {
        return this.tryLogin(options);
      });
    }
    /**
     * Convenience method that first calls `loadDiscoveryDocumentAndTryLogin(...)`
     * and if then chains to `initLoginFlow()`, but only if there is no valid
     * IdToken or no valid AccessToken.
     *
     * @param options LoginOptions to pass through to `tryLogin(...)`
     */
    loadDiscoveryDocumentAndLogin(options = null) {
      options = options || {};
      return this.loadDiscoveryDocumentAndTryLogin(options).then(() => {
        if (!this.hasValidIdToken() || !this.hasValidAccessToken()) {
          const state = typeof options.state === 'string' ? options.state : '';
          this.initLoginFlow(state);
          return false;
        } else {
          return true;
        }
      });
    }
    debug(...args) {
      if (this.showDebugInformation) {
        this.logger.debug(...args);
      }
    }
    validateUrlFromDiscoveryDocument(url) {
      const errors = [];
      const httpsCheck = this.validateUrlForHttps(url);
      const issuerCheck = this.validateUrlAgainstIssuer(url);
      if (!httpsCheck) {
        errors.push('https for all urls required. Also for urls received by discovery.');
      }
      if (!issuerCheck) {
        errors.push('Every url in discovery document has to start with the issuer url.' + 'Also see property strictDiscoveryDocumentValidation.');
      }
      return errors;
    }
    validateUrlForHttps(url) {
      if (!url) {
        return true;
      }
      const lcUrl = url.toLowerCase();
      if (this.requireHttps === false) {
        return true;
      }
      if ((lcUrl.match(/^http:\/\/localhost($|[:/])/) || lcUrl.match(/^http:\/\/localhost($|[:/])/)) && this.requireHttps === 'remoteOnly') {
        return true;
      }
      return lcUrl.startsWith('https://');
    }
    assertUrlNotNullAndCorrectProtocol(url, description) {
      if (!url) {
        throw new Error(`'${description}' should not be null`);
      }
      if (!this.validateUrlForHttps(url)) {
        throw new Error(`'${description}' must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).`);
      }
    }
    validateUrlAgainstIssuer(url) {
      if (!this.strictDiscoveryDocumentValidation) {
        return true;
      }
      if (!url) {
        return true;
      }
      return url.toLowerCase().startsWith(this.issuer.toLowerCase());
    }
    setupRefreshTimer() {
      if (typeof window === 'undefined') {
        this.debug('timer not supported on this plattform');
        return;
      }
      if (this.hasValidIdToken() || this.hasValidAccessToken()) {
        this.clearAccessTokenTimer();
        this.clearIdTokenTimer();
        this.setupExpirationTimers();
      }
      if (this.tokenReceivedSubscription) this.tokenReceivedSubscription.unsubscribe();
      this.tokenReceivedSubscription = this.events.pipe(filter(e => e.type === 'token_received')).subscribe(() => {
        this.clearAccessTokenTimer();
        this.clearIdTokenTimer();
        this.setupExpirationTimers();
      });
    }
    setupExpirationTimers() {
      if (this.hasValidAccessToken()) {
        this.setupAccessTokenTimer();
      }
      if (!this.disableIdTokenTimer && this.hasValidIdToken()) {
        this.setupIdTokenTimer();
      }
    }
    setupAccessTokenTimer() {
      const expiration = this.getAccessTokenExpiration();
      const storedAt = this.getAccessTokenStoredAt();
      const timeout = this.calcTimeout(storedAt, expiration);
      this.ngZone.runOutsideAngular(() => {
        this.accessTokenTimeoutSubscription = of(new OAuthInfoEvent('token_expires', 'access_token')).pipe(delay(timeout)).subscribe(e => {
          this.ngZone.run(() => {
            this.eventsSubject.next(e);
          });
        });
      });
    }
    setupIdTokenTimer() {
      const expiration = this.getIdTokenExpiration();
      const storedAt = this.getIdTokenStoredAt();
      const timeout = this.calcTimeout(storedAt, expiration);
      this.ngZone.runOutsideAngular(() => {
        this.idTokenTimeoutSubscription = of(new OAuthInfoEvent('token_expires', 'id_token')).pipe(delay(timeout)).subscribe(e => {
          this.ngZone.run(() => {
            this.eventsSubject.next(e);
          });
        });
      });
    }
    /**
     * Stops timers for automatic refresh.
     * To restart it, call setupAutomaticSilentRefresh again.
     */
    stopAutomaticRefresh() {
      this.clearAccessTokenTimer();
      this.clearIdTokenTimer();
      this.clearAutomaticRefreshTimer();
    }
    clearAccessTokenTimer() {
      if (this.accessTokenTimeoutSubscription) {
        this.accessTokenTimeoutSubscription.unsubscribe();
      }
    }
    clearIdTokenTimer() {
      if (this.idTokenTimeoutSubscription) {
        this.idTokenTimeoutSubscription.unsubscribe();
      }
    }
    clearAutomaticRefreshTimer() {
      if (this.automaticRefreshSubscription) {
        this.automaticRefreshSubscription.unsubscribe();
      }
    }
    calcTimeout(storedAt, expiration) {
      const now = this.dateTimeService.now();
      const delta = (expiration - storedAt) * this.timeoutFactor - (now - storedAt);
      const duration = Math.max(0, delta);
      const maxTimeoutValue = 2_147_483_647;
      return duration > maxTimeoutValue ? maxTimeoutValue : duration;
    }
    /**
     * DEPRECATED. Use a provider for OAuthStorage instead:
     *
     * { provide: OAuthStorage, useFactory: oAuthStorageFactory }
     * export function oAuthStorageFactory(): OAuthStorage { return localStorage; }
     * Sets a custom storage used to store the received
     * tokens on client side. By default, the browser's
     * sessionStorage is used.
     * @ignore
     *
     * @param storage
     */
    setStorage(storage) {
      this._storage = storage;
      this.configChanged();
    }
    /**
     * Loads the discovery document to configure most
     * properties of this service. The url of the discovery
     * document is infered from the issuer's url according
     * to the OpenId Connect spec. To use another url you
     * can pass it to to optional parameter fullUrl.
     *
     * @param fullUrl
     */
    loadDiscoveryDocument(fullUrl = null) {
      return new Promise((resolve, reject) => {
        if (!fullUrl) {
          fullUrl = this.issuer || '';
          if (!fullUrl.endsWith('/')) {
            fullUrl += '/';
          }
          fullUrl += '.well-known/openid-configuration';
        }
        if (!this.validateUrlForHttps(fullUrl)) {
          reject("issuer  must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
          return;
        }
        this.http.get(fullUrl).subscribe(doc => {
          if (!this.validateDiscoveryDocument(doc)) {
            this.eventsSubject.next(new OAuthErrorEvent('discovery_document_validation_error', null));
            reject('discovery_document_validation_error');
            return;
          }
          this.loginUrl = doc.authorization_endpoint;
          this.logoutUrl = doc.end_session_endpoint || this.logoutUrl;
          this.grantTypesSupported = doc.grant_types_supported;
          this.issuer = doc.issuer;
          this.tokenEndpoint = doc.token_endpoint;
          this.userinfoEndpoint = doc.userinfo_endpoint || this.userinfoEndpoint;
          this.jwksUri = doc.jwks_uri;
          this.sessionCheckIFrameUrl = doc.check_session_iframe || this.sessionCheckIFrameUrl;
          this.discoveryDocumentLoaded = true;
          this.discoveryDocumentLoadedSubject.next(doc);
          this.revocationEndpoint = doc.revocation_endpoint || this.revocationEndpoint;
          if (this.sessionChecksEnabled) {
            this.restartSessionChecksIfStillLoggedIn();
          }
          this.loadJwks().then(jwks => {
            const result = {
              discoveryDocument: doc,
              jwks: jwks
            };
            const event = new OAuthSuccessEvent('discovery_document_loaded', result);
            this.eventsSubject.next(event);
            resolve(event);
            return;
          }).catch(err => {
            this.eventsSubject.next(new OAuthErrorEvent('discovery_document_load_error', err));
            reject(err);
            return;
          });
        }, err => {
          this.logger.error('error loading discovery document', err);
          this.eventsSubject.next(new OAuthErrorEvent('discovery_document_load_error', err));
          reject(err);
        });
      });
    }
    loadJwks() {
      return new Promise((resolve, reject) => {
        if (this.jwksUri) {
          this.http.get(this.jwksUri).subscribe(jwks => {
            this.jwks = jwks;
            // this.eventsSubject.next(
            //   new OAuthSuccessEvent('discovery_document_loaded')
            // );
            resolve(jwks);
          }, err => {
            this.logger.error('error loading jwks', err);
            this.eventsSubject.next(new OAuthErrorEvent('jwks_load_error', err));
            reject(err);
          });
        } else {
          resolve(null);
        }
      });
    }
    validateDiscoveryDocument(doc) {
      let errors;
      if (!this.skipIssuerCheck && doc.issuer !== this.issuer) {
        this.logger.error('invalid issuer in discovery document', 'expected: ' + this.issuer, 'current: ' + doc.issuer);
        return false;
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.authorization_endpoint);
      if (errors.length > 0) {
        this.logger.error('error validating authorization_endpoint in discovery document', errors);
        return false;
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.end_session_endpoint);
      if (errors.length > 0) {
        this.logger.error('error validating end_session_endpoint in discovery document', errors);
        return false;
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.token_endpoint);
      if (errors.length > 0) {
        this.logger.error('error validating token_endpoint in discovery document', errors);
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.revocation_endpoint);
      if (errors.length > 0) {
        this.logger.error('error validating revocation_endpoint in discovery document', errors);
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.userinfo_endpoint);
      if (errors.length > 0) {
        this.logger.error('error validating userinfo_endpoint in discovery document', errors);
        return false;
      }
      errors = this.validateUrlFromDiscoveryDocument(doc.jwks_uri);
      if (errors.length > 0) {
        this.logger.error('error validating jwks_uri in discovery document', errors);
        return false;
      }
      if (this.sessionChecksEnabled && !doc.check_session_iframe) {
        this.logger.warn('sessionChecksEnabled is activated but discovery document' + ' does not contain a check_session_iframe field');
      }
      return true;
    }
    /**
     * Uses password flow to exchange userName and password for an
     * access_token. After receiving the access_token, this method
     * uses it to query the userinfo endpoint in order to get information
     * about the user in question.
     *
     * When using this, make sure that the property oidc is set to false.
     * Otherwise stricter validations take place that make this operation
     * fail.
     *
     * @param userName
     * @param password
     * @param headers Optional additional http-headers.
     */
    fetchTokenUsingPasswordFlowAndLoadUserProfile(userName, password, headers = new HttpHeaders()) {
      return this.fetchTokenUsingPasswordFlow(userName, password, headers).then(() => this.loadUserProfile());
    }
    /**
     * Loads the user profile by accessing the user info endpoint defined by OpenId Connect.
     *
     * When using this with OAuth2 password flow, make sure that the property oidc is set to false.
     * Otherwise stricter validations take place that make this operation fail.
     */
    loadUserProfile() {
      if (!this.hasValidAccessToken()) {
        throw new Error('Can not load User Profile without access_token');
      }
      if (!this.validateUrlForHttps(this.userinfoEndpoint)) {
        throw new Error("userinfoEndpoint must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
      }
      return new Promise((resolve, reject) => {
        const headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.getAccessToken());
        this.http.get(this.userinfoEndpoint, {
          headers,
          observe: 'response',
          responseType: 'text'
        }).subscribe(response => {
          this.debug('userinfo received', JSON.stringify(response));
          if (response.headers.get('content-type').startsWith('application/json')) {
            let info = JSON.parse(response.body);
            const existingClaims = this.getIdentityClaims() || {};
            if (!this.skipSubjectCheck) {
              if (this.oidc && (!existingClaims['sub'] || info.sub !== existingClaims['sub'])) {
                const err = 'if property oidc is true, the received user-id (sub) has to be the user-id ' + 'of the user that has logged in with oidc.\n' + 'if you are not using oidc but just oauth2 password flow set oidc to false';
                reject(err);
                return;
              }
            }
            info = Object.assign({}, existingClaims, info);
            this._storage.setItem('id_token_claims_obj', JSON.stringify(info));
            this.eventsSubject.next(new OAuthSuccessEvent('user_profile_loaded'));
            resolve({
              info
            });
          } else {
            this.debug('userinfo is not JSON, treating it as JWE/JWS');
            this.eventsSubject.next(new OAuthSuccessEvent('user_profile_loaded'));
            resolve(JSON.parse(response.body));
          }
        }, err => {
          this.logger.error('error loading user info', err);
          this.eventsSubject.next(new OAuthErrorEvent('user_profile_load_error', err));
          reject(err);
        });
      });
    }
    /**
     * Uses password flow to exchange userName and password for an access_token.
     * @param userName
     * @param password
     * @param headers Optional additional http-headers.
     */
    fetchTokenUsingPasswordFlow(userName, password, headers = new HttpHeaders()) {
      const parameters = {
        username: userName,
        password: password
      };
      return this.fetchTokenUsingGrant('password', parameters, headers);
    }
    /**
     * Uses a custom grant type to retrieve tokens.
     * @param grantType Grant type.
     * @param parameters Parameters to pass.
     * @param headers Optional additional HTTP headers.
     */
    fetchTokenUsingGrant(grantType, parameters, headers = new HttpHeaders()) {
      this.assertUrlNotNullAndCorrectProtocol(this.tokenEndpoint, 'tokenEndpoint');
      /**
       * A `HttpParameterCodec` that uses `encodeURIComponent` and `decodeURIComponent` to
       * serialize and parse URL parameter keys and values.
       *
       * @stable
       */
      let params = new HttpParams({
        encoder: new WebHttpUrlEncodingCodec()
      }).set('grant_type', grantType).set('scope', this.scope);
      if (this.useHttpBasicAuth) {
        const header = btoa(`${this.clientId}:${this.dummyClientSecret}`);
        headers = headers.set('Authorization', 'Basic ' + header);
      }
      if (!this.useHttpBasicAuth) {
        params = params.set('client_id', this.clientId);
      }
      if (!this.useHttpBasicAuth && this.dummyClientSecret) {
        params = params.set('client_secret', this.dummyClientSecret);
      }
      if (this.customQueryParams) {
        for (const key of Object.getOwnPropertyNames(this.customQueryParams)) {
          params = params.set(key, this.customQueryParams[key]);
        }
      }
      // set explicit parameters last, to allow overwriting
      for (const key of Object.keys(parameters)) {
        params = params.set(key, parameters[key]);
      }
      headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return new Promise((resolve, reject) => {
        this.http.post(this.tokenEndpoint, params, {
          headers
        }).subscribe(tokenResponse => {
          this.debug('tokenResponse', tokenResponse);
          this.storeAccessTokenResponse(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.expires_in || this.fallbackAccessTokenExpirationTimeInSec, tokenResponse.scope, this.extractRecognizedCustomParameters(tokenResponse));
          if (this.oidc && tokenResponse.id_token) {
            this.processIdToken(tokenResponse.id_token, tokenResponse.access_token).then(result => {
              this.storeIdToken(result);
              resolve(tokenResponse);
            });
          }
          this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
          resolve(tokenResponse);
        }, err => {
          this.logger.error('Error performing ${grantType} flow', err);
          this.eventsSubject.next(new OAuthErrorEvent('token_error', err));
          reject(err);
        });
      });
    }
    /**
     * Refreshes the token using a refresh_token.
     * This does not work for implicit flow, b/c
     * there is no refresh_token in this flow.
     * A solution for this is provided by the
     * method silentRefresh.
     */
    refreshToken() {
      this.assertUrlNotNullAndCorrectProtocol(this.tokenEndpoint, 'tokenEndpoint');
      return new Promise((resolve, reject) => {
        let params = new HttpParams({
          encoder: new WebHttpUrlEncodingCodec()
        }).set('grant_type', 'refresh_token').set('scope', this.scope).set('refresh_token', this._storage.getItem('refresh_token'));
        let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
        if (this.useHttpBasicAuth) {
          const header = btoa(`${this.clientId}:${this.dummyClientSecret}`);
          headers = headers.set('Authorization', 'Basic ' + header);
        }
        if (!this.useHttpBasicAuth) {
          params = params.set('client_id', this.clientId);
        }
        if (!this.useHttpBasicAuth && this.dummyClientSecret) {
          params = params.set('client_secret', this.dummyClientSecret);
        }
        if (this.customQueryParams) {
          for (const key of Object.getOwnPropertyNames(this.customQueryParams)) {
            params = params.set(key, this.customQueryParams[key]);
          }
        }
        this.http.post(this.tokenEndpoint, params, {
          headers
        }).pipe(switchMap(tokenResponse => {
          if (this.oidc && tokenResponse.id_token) {
            return from(this.processIdToken(tokenResponse.id_token, tokenResponse.access_token, true)).pipe(tap(result => this.storeIdToken(result)), map(() => tokenResponse));
          } else {
            return of(tokenResponse);
          }
        })).subscribe(tokenResponse => {
          this.debug('refresh tokenResponse', tokenResponse);
          this.storeAccessTokenResponse(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.expires_in || this.fallbackAccessTokenExpirationTimeInSec, tokenResponse.scope, this.extractRecognizedCustomParameters(tokenResponse));
          this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
          this.eventsSubject.next(new OAuthSuccessEvent('token_refreshed'));
          resolve(tokenResponse);
        }, err => {
          this.logger.error('Error refreshing token', err);
          this.eventsSubject.next(new OAuthErrorEvent('token_refresh_error', err));
          reject(err);
        });
      });
    }
    removeSilentRefreshEventListener() {
      if (this.silentRefreshPostMessageEventListener) {
        window.removeEventListener('message', this.silentRefreshPostMessageEventListener);
        this.silentRefreshPostMessageEventListener = null;
      }
    }
    setupSilentRefreshEventListener() {
      this.removeSilentRefreshEventListener();
      this.silentRefreshPostMessageEventListener = e => {
        const message = this.processMessageEventMessage(e);
        if (this.checkOrigin && e.origin !== location.origin) {
          console.error('wrong origin requested silent refresh!');
        }
        this.tryLogin({
          customHashFragment: message,
          preventClearHashAfterLogin: true,
          customRedirectUri: this.silentRefreshRedirectUri || this.redirectUri
        }).catch(err => this.debug('tryLogin during silent refresh failed', err));
      };
      window.addEventListener('message', this.silentRefreshPostMessageEventListener);
    }
    /**
     * Performs a silent refresh for implicit flow.
     * Use this method to get new tokens when/before
     * the existing tokens expire.
     */
    silentRefresh(params = {}, noPrompt = true) {
      const claims = this.getIdentityClaims() || {};
      if (this.useIdTokenHintForSilentRefresh && this.hasValidIdToken()) {
        params['id_token_hint'] = this.getIdToken();
      }
      if (!this.validateUrlForHttps(this.loginUrl)) {
        throw new Error("loginUrl  must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
      }
      if (typeof this.document === 'undefined') {
        throw new Error('silent refresh is not supported on this platform');
      }
      const existingIframe = this.document.getElementById(this.silentRefreshIFrameName);
      if (existingIframe) {
        this.document.body.removeChild(existingIframe);
      }
      this.silentRefreshSubject = claims['sub'];
      const iframe = this.document.createElement('iframe');
      iframe.id = this.silentRefreshIFrameName;
      this.setupSilentRefreshEventListener();
      const redirectUri = this.silentRefreshRedirectUri || this.redirectUri;
      this.createLoginUrl(null, null, redirectUri, noPrompt, params).then(url => {
        iframe.setAttribute('src', url);
        if (!this.silentRefreshShowIFrame) {
          iframe.style['display'] = 'none';
        }
        this.document.body.appendChild(iframe);
      });
      const errors = this.events.pipe(filter(e => e instanceof OAuthErrorEvent), first());
      const success = this.events.pipe(filter(e => e.type === 'token_received'), first());
      const timeout = of(new OAuthErrorEvent('silent_refresh_timeout', null)).pipe(delay(this.silentRefreshTimeout));
      return race([errors, success, timeout]).pipe(map(e => {
        if (e instanceof OAuthErrorEvent) {
          if (e.type === 'silent_refresh_timeout') {
            this.eventsSubject.next(e);
          } else {
            e = new OAuthErrorEvent('silent_refresh_error', e);
            this.eventsSubject.next(e);
          }
          throw e;
        } else if (e.type === 'token_received') {
          e = new OAuthSuccessEvent('silently_refreshed');
          this.eventsSubject.next(e);
        }
        return e;
      })).toPromise();
    }
    /**
     * This method exists for backwards compatibility.
     * {@link OAuthService#initLoginFlowInPopup} handles both code
     * and implicit flows.
     */
    initImplicitFlowInPopup(options) {
      return this.initLoginFlowInPopup(options);
    }
    initLoginFlowInPopup(options) {
      options = options || {};
      return this.createLoginUrl(null, null, this.silentRefreshRedirectUri, false, {
        display: 'popup'
      }).then(url => {
        return new Promise((resolve, reject) => {
          /**
           * Error handling section
           */
          const checkForPopupClosedInterval = 500;
          let windowRef = null;
          // If we got no window reference we open a window
          // else we are using the window already opened
          if (!options.windowRef) {
            windowRef = window.open(url, 'ngx-oauth2-oidc-login', this.calculatePopupFeatures(options));
          } else if (options.windowRef && !options.windowRef.closed) {
            windowRef = options.windowRef;
            windowRef.location.href = url;
          }
          let checkForPopupClosedTimer;
          const tryLogin = hash => {
            this.tryLogin({
              customHashFragment: hash,
              preventClearHashAfterLogin: true,
              customRedirectUri: this.silentRefreshRedirectUri
            }).then(() => {
              cleanup();
              resolve(true);
            }, err => {
              cleanup();
              reject(err);
            });
          };
          const checkForPopupClosed = () => {
            if (!windowRef || windowRef.closed) {
              cleanup();
              reject(new OAuthErrorEvent('popup_closed', {}));
            }
          };
          if (!windowRef) {
            reject(new OAuthErrorEvent('popup_blocked', {}));
          } else {
            checkForPopupClosedTimer = window.setInterval(checkForPopupClosed, checkForPopupClosedInterval);
          }
          const cleanup = () => {
            window.clearInterval(checkForPopupClosedTimer);
            window.removeEventListener('storage', storageListener);
            window.removeEventListener('message', listener);
            if (windowRef !== null) {
              windowRef.close();
            }
            windowRef = null;
          };
          const listener = e => {
            const message = this.processMessageEventMessage(e);
            if (message && message !== null) {
              window.removeEventListener('storage', storageListener);
              tryLogin(message);
            } else {
              console.log('false event firing');
            }
          };
          const storageListener = event => {
            if (event.key === 'auth_hash') {
              window.removeEventListener('message', listener);
              tryLogin(event.newValue);
            }
          };
          window.addEventListener('message', listener);
          window.addEventListener('storage', storageListener);
        });
      });
    }
    calculatePopupFeatures(options) {
      // Specify an static height and width and calculate centered position
      const height = options.height || 470;
      const width = options.width || 500;
      const left = window.screenLeft + (window.outerWidth - width) / 2;
      const top = window.screenTop + (window.outerHeight - height) / 2;
      return `location=no,toolbar=no,width=${width},height=${height},top=${top},left=${left}`;
    }
    processMessageEventMessage(e) {
      let expectedPrefix = '#';
      if (this.silentRefreshMessagePrefix) {
        expectedPrefix += this.silentRefreshMessagePrefix;
      }
      if (!e || !e.data || typeof e.data !== 'string') {
        return;
      }
      const prefixedMessage = e.data;
      if (!prefixedMessage.startsWith(expectedPrefix)) {
        return;
      }
      return '#' + prefixedMessage.substr(expectedPrefix.length);
    }
    canPerformSessionCheck() {
      if (!this.sessionChecksEnabled) {
        return false;
      }
      if (!this.sessionCheckIFrameUrl) {
        console.warn('sessionChecksEnabled is activated but there is no sessionCheckIFrameUrl');
        return false;
      }
      const sessionState = this.getSessionState();
      if (!sessionState) {
        console.warn('sessionChecksEnabled is activated but there is no session_state');
        return false;
      }
      if (typeof this.document === 'undefined') {
        return false;
      }
      return true;
    }
    setupSessionCheckEventListener() {
      this.removeSessionCheckEventListener();
      this.sessionCheckEventListener = e => {
        const origin = e.origin.toLowerCase();
        const issuer = this.issuer.toLowerCase();
        this.debug('sessionCheckEventListener');
        if (!issuer.startsWith(origin)) {
          this.debug('sessionCheckEventListener', 'wrong origin', origin, 'expected', issuer, 'event', e);
          return;
        }
        // only run in Angular zone if it is 'changed' or 'error'
        switch (e.data) {
          case 'unchanged':
            this.ngZone.run(() => {
              this.handleSessionUnchanged();
            });
            break;
          case 'changed':
            this.ngZone.run(() => {
              this.handleSessionChange();
            });
            break;
          case 'error':
            this.ngZone.run(() => {
              this.handleSessionError();
            });
            break;
        }
        this.debug('got info from session check inframe', e);
      };
      // prevent Angular from refreshing the view on every message (runs in intervals)
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('message', this.sessionCheckEventListener);
      });
    }
    handleSessionUnchanged() {
      this.debug('session check', 'session unchanged');
      this.eventsSubject.next(new OAuthInfoEvent('session_unchanged'));
    }
    handleSessionChange() {
      this.eventsSubject.next(new OAuthInfoEvent('session_changed'));
      this.stopSessionCheckTimer();
      if (!this.useSilentRefresh && this.responseType === 'code') {
        this.refreshToken().then(() => {
          this.debug('token refresh after session change worked');
        }).catch(() => {
          this.debug('token refresh did not work after session changed');
          this.eventsSubject.next(new OAuthInfoEvent('session_terminated'));
          this.logOut(true);
        });
      } else if (this.silentRefreshRedirectUri) {
        this.silentRefresh().catch(() => this.debug('silent refresh failed after session changed'));
        this.waitForSilentRefreshAfterSessionChange();
      } else {
        this.eventsSubject.next(new OAuthInfoEvent('session_terminated'));
        this.logOut(true);
      }
    }
    waitForSilentRefreshAfterSessionChange() {
      this.events.pipe(filter(e => e.type === 'silently_refreshed' || e.type === 'silent_refresh_timeout' || e.type === 'silent_refresh_error'), first()).subscribe(e => {
        if (e.type !== 'silently_refreshed') {
          this.debug('silent refresh did not work after session changed');
          this.eventsSubject.next(new OAuthInfoEvent('session_terminated'));
          this.logOut(true);
        }
      });
    }
    handleSessionError() {
      this.stopSessionCheckTimer();
      this.eventsSubject.next(new OAuthInfoEvent('session_error'));
    }
    removeSessionCheckEventListener() {
      if (this.sessionCheckEventListener) {
        window.removeEventListener('message', this.sessionCheckEventListener);
        this.sessionCheckEventListener = null;
      }
    }
    initSessionCheck() {
      if (!this.canPerformSessionCheck()) {
        return;
      }
      const existingIframe = this.document.getElementById(this.sessionCheckIFrameName);
      if (existingIframe) {
        this.document.body.removeChild(existingIframe);
      }
      const iframe = this.document.createElement('iframe');
      iframe.id = this.sessionCheckIFrameName;
      this.setupSessionCheckEventListener();
      const url = this.sessionCheckIFrameUrl;
      iframe.setAttribute('src', url);
      iframe.style.display = 'none';
      this.document.body.appendChild(iframe);
      this.startSessionCheckTimer();
    }
    startSessionCheckTimer() {
      this.stopSessionCheckTimer();
      this.ngZone.runOutsideAngular(() => {
        this.sessionCheckTimer = setInterval(this.checkSession.bind(this), this.sessionCheckIntervall);
      });
    }
    stopSessionCheckTimer() {
      if (this.sessionCheckTimer) {
        clearInterval(this.sessionCheckTimer);
        this.sessionCheckTimer = null;
      }
    }
    checkSession() {
      const iframe = this.document.getElementById(this.sessionCheckIFrameName);
      if (!iframe) {
        this.logger.warn('checkSession did not find iframe', this.sessionCheckIFrameName);
      }
      const sessionState = this.getSessionState();
      if (!sessionState) {
        this.stopSessionCheckTimer();
      }
      const message = this.clientId + ' ' + sessionState;
      iframe.contentWindow.postMessage(message, this.issuer);
    }
    async createLoginUrl(state = '', loginHint = '', customRedirectUri = '', noPrompt = false, params = {}) {
      const that = this; // eslint-disable-line @typescript-eslint/no-this-alias
      let redirectUri;
      if (customRedirectUri) {
        redirectUri = customRedirectUri;
      } else {
        redirectUri = this.redirectUri;
      }
      const nonce = await this.createAndSaveNonce();
      if (state) {
        state = nonce + this.config.nonceStateSeparator + encodeURIComponent(state);
      } else {
        state = nonce;
      }
      if (!this.requestAccessToken && !this.oidc) {
        throw new Error('Either requestAccessToken or oidc or both must be true');
      }
      if (this.config.responseType) {
        this.responseType = this.config.responseType;
      } else {
        if (this.oidc && this.requestAccessToken) {
          this.responseType = 'id_token token';
        } else if (this.oidc && !this.requestAccessToken) {
          this.responseType = 'id_token';
        } else {
          this.responseType = 'token';
        }
      }
      const seperationChar = that.loginUrl.indexOf('?') > -1 ? '&' : '?';
      let scope = that.scope;
      if (this.oidc && !scope.match(/(^|\s)openid($|\s)/)) {
        scope = 'openid ' + scope;
      }
      let url = that.loginUrl + seperationChar + 'response_type=' + encodeURIComponent(that.responseType) + '&client_id=' + encodeURIComponent(that.clientId) + '&state=' + encodeURIComponent(state) + '&redirect_uri=' + encodeURIComponent(redirectUri) + '&scope=' + encodeURIComponent(scope);
      if (this.responseType.includes('code') && !this.disablePKCE) {
        const [challenge, verifier] = await this.createChallangeVerifierPairForPKCE();
        if (this.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined') {
          localStorage.setItem('PKCE_verifier', verifier);
        } else {
          this._storage.setItem('PKCE_verifier', verifier);
        }
        url += '&code_challenge=' + challenge;
        url += '&code_challenge_method=S256';
      }
      if (loginHint) {
        url += '&login_hint=' + encodeURIComponent(loginHint);
      }
      if (that.resource) {
        url += '&resource=' + encodeURIComponent(that.resource);
      }
      if (that.oidc) {
        url += '&nonce=' + encodeURIComponent(nonce);
      }
      if (noPrompt) {
        url += '&prompt=none';
      }
      for (const key of Object.keys(params)) {
        url += '&' + encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
      }
      if (this.customQueryParams) {
        for (const key of Object.getOwnPropertyNames(this.customQueryParams)) {
          url += '&' + key + '=' + encodeURIComponent(this.customQueryParams[key]);
        }
      }
      return url;
    }
    initImplicitFlowInternal(additionalState = '', params = '') {
      if (this.inImplicitFlow) {
        return;
      }
      this.inImplicitFlow = true;
      if (!this.validateUrlForHttps(this.loginUrl)) {
        throw new Error("loginUrl  must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
      }
      let addParams = {};
      let loginHint = null;
      if (typeof params === 'string') {
        loginHint = params;
      } else if (typeof params === 'object') {
        addParams = params;
      }
      this.createLoginUrl(additionalState, loginHint, null, false, addParams).then(this.config.openUri).catch(error => {
        console.error('Error in initImplicitFlow', error);
        this.inImplicitFlow = false;
      });
    }
    /**
     * Starts the implicit flow and redirects to user to
     * the auth servers' login url.
     *
     * @param additionalState Optional state that is passed around.
     *  You'll find this state in the property `state` after `tryLogin` logged in the user.
     * @param params Hash with additional parameter. If it is a string, it is used for the
     *               parameter loginHint (for the sake of compatibility with former versions)
     */
    initImplicitFlow(additionalState = '', params = '') {
      if (this.loginUrl !== '') {
        this.initImplicitFlowInternal(additionalState, params);
      } else {
        this.events.pipe(filter(e => e.type === 'discovery_document_loaded')).subscribe(() => this.initImplicitFlowInternal(additionalState, params));
      }
    }
    /**
     * Reset current implicit flow
     *
     * @description This method allows resetting the current implict flow in order to be initialized again.
     */
    resetImplicitFlow() {
      this.inImplicitFlow = false;
    }
    callOnTokenReceivedIfExists(options) {
      const that = this; // eslint-disable-line @typescript-eslint/no-this-alias
      if (options.onTokenReceived) {
        const tokenParams = {
          idClaims: that.getIdentityClaims(),
          idToken: that.getIdToken(),
          accessToken: that.getAccessToken(),
          state: that.state
        };
        options.onTokenReceived(tokenParams);
      }
    }
    storeAccessTokenResponse(accessToken, refreshToken, expiresIn, grantedScopes, customParameters) {
      this._storage.setItem('access_token', accessToken);
      if (grantedScopes && !Array.isArray(grantedScopes)) {
        this._storage.setItem('granted_scopes', JSON.stringify(grantedScopes.split(' ')));
      } else if (grantedScopes && Array.isArray(grantedScopes)) {
        this._storage.setItem('granted_scopes', JSON.stringify(grantedScopes));
      }
      this._storage.setItem('access_token_stored_at', '' + this.dateTimeService.now());
      if (expiresIn) {
        const expiresInMilliSeconds = expiresIn * 1000;
        const now = this.dateTimeService.new();
        const expiresAt = now.getTime() + expiresInMilliSeconds;
        this._storage.setItem('expires_at', '' + expiresAt);
      }
      if (refreshToken) {
        this._storage.setItem('refresh_token', refreshToken);
      }
      if (customParameters) {
        customParameters.forEach((value, key) => {
          this._storage.setItem(key, value);
        });
      }
    }
    /**
     * Delegates to tryLoginImplicitFlow for the sake of competability
     * @param options Optional options.
     */
    tryLogin(options = null) {
      if (this.config.responseType === 'code') {
        return this.tryLoginCodeFlow(options).then(() => true);
      } else {
        return this.tryLoginImplicitFlow(options);
      }
    }
    parseQueryString(queryString) {
      if (!queryString || queryString.length === 0) {
        return {};
      }
      if (queryString.charAt(0) === '?') {
        queryString = queryString.substr(1);
      }
      return this.urlHelper.parseQueryString(queryString);
    }
    async tryLoginCodeFlow(options = null) {
      options = options || {};
      const querySource = options.customHashFragment ? options.customHashFragment.substring(1) : window.location.search;
      const parts = this.getCodePartsFromUrl(querySource);
      const code = parts['code'];
      const state = parts['state'];
      const sessionState = parts['session_state'];
      if (!options.preventClearHashAfterLogin) {
        const href = location.origin + location.pathname + location.search.replace(/code=[^&$]*/, '').replace(/scope=[^&$]*/, '').replace(/state=[^&$]*/, '').replace(/session_state=[^&$]*/, '').replace(/^\?&/, '?').replace(/&$/, '').replace(/^\?$/, '').replace(/&+/g, '&').replace(/\?&/, '?').replace(/\?$/, '') + location.hash;
        history.replaceState(null, window.name, href);
      }
      const [nonceInState, userState] = this.parseState(state);
      this.state = userState;
      if (parts['error']) {
        this.debug('error trying to login');
        this.handleLoginError(options, parts);
        const err = new OAuthErrorEvent('code_error', {}, parts);
        this.eventsSubject.next(err);
        return Promise.reject(err);
      }
      if (!options.disableNonceCheck) {
        if (!nonceInState) {
          this.saveRequestedRoute();
          return Promise.resolve();
        }
        if (!options.disableOAuth2StateCheck) {
          const success = this.validateNonce(nonceInState);
          if (!success) {
            const event = new OAuthErrorEvent('invalid_nonce_in_state', null);
            this.eventsSubject.next(event);
            return Promise.reject(event);
          }
        }
      }
      this.storeSessionState(sessionState);
      if (code) {
        await this.getTokenFromCode(code, options);
        this.restoreRequestedRoute();
        return Promise.resolve();
      } else {
        return Promise.resolve();
      }
    }
    saveRequestedRoute() {
      if (this.config.preserveRequestedRoute) {
        this._storage.setItem('requested_route', window.location.pathname + window.location.search);
      }
    }
    restoreRequestedRoute() {
      const requestedRoute = this._storage.getItem('requested_route');
      if (requestedRoute) {
        history.replaceState(null, '', window.location.origin + requestedRoute);
      }
    }
    /**
     * Retrieve the returned auth code from the redirect uri that has been called.
     * If required also check hash, as we could use hash location strategy.
     */
    getCodePartsFromUrl(queryString) {
      if (!queryString || queryString.length === 0) {
        return this.urlHelper.getHashFragmentParams();
      }
      // normalize query string
      if (queryString.charAt(0) === '?') {
        queryString = queryString.substr(1);
      }
      return this.urlHelper.parseQueryString(queryString);
    }
    /**
     * Get token using an intermediate code. Works for the Authorization Code flow.
     */
    getTokenFromCode(code, options) {
      let params = new HttpParams({
        encoder: new WebHttpUrlEncodingCodec()
      }).set('grant_type', 'authorization_code').set('code', code).set('redirect_uri', options.customRedirectUri || this.redirectUri);
      if (!this.disablePKCE) {
        let PKCEVerifier;
        if (this.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined') {
          PKCEVerifier = localStorage.getItem('PKCE_verifier');
        } else {
          PKCEVerifier = this._storage.getItem('PKCE_verifier');
        }
        if (!PKCEVerifier) {
          console.warn('No PKCE verifier found in oauth storage!');
        } else {
          params = params.set('code_verifier', PKCEVerifier);
        }
      }
      return this.fetchAndProcessToken(params, options);
    }
    fetchAndProcessToken(params, options) {
      options = options || {};
      this.assertUrlNotNullAndCorrectProtocol(this.tokenEndpoint, 'tokenEndpoint');
      let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      if (this.useHttpBasicAuth) {
        const header = btoa(`${this.clientId}:${this.dummyClientSecret}`);
        headers = headers.set('Authorization', 'Basic ' + header);
      }
      if (!this.useHttpBasicAuth) {
        params = params.set('client_id', this.clientId);
      }
      if (!this.useHttpBasicAuth && this.dummyClientSecret) {
        params = params.set('client_secret', this.dummyClientSecret);
      }
      return new Promise((resolve, reject) => {
        if (this.customQueryParams) {
          for (const key of Object.getOwnPropertyNames(this.customQueryParams)) {
            params = params.set(key, this.customQueryParams[key]);
          }
        }
        this.http.post(this.tokenEndpoint, params, {
          headers
        }).subscribe(tokenResponse => {
          this.debug('refresh tokenResponse', tokenResponse);
          this.storeAccessTokenResponse(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.expires_in || this.fallbackAccessTokenExpirationTimeInSec, tokenResponse.scope, this.extractRecognizedCustomParameters(tokenResponse));
          if (this.oidc && tokenResponse.id_token) {
            this.processIdToken(tokenResponse.id_token, tokenResponse.access_token, options.disableNonceCheck).then(result => {
              this.storeIdToken(result);
              this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
              this.eventsSubject.next(new OAuthSuccessEvent('token_refreshed'));
              resolve(tokenResponse);
            }).catch(reason => {
              this.eventsSubject.next(new OAuthErrorEvent('token_validation_error', reason));
              console.error('Error validating tokens');
              console.error(reason);
              reject(reason);
            });
          } else {
            this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
            this.eventsSubject.next(new OAuthSuccessEvent('token_refreshed'));
            resolve(tokenResponse);
          }
        }, err => {
          console.error('Error getting token', err);
          this.eventsSubject.next(new OAuthErrorEvent('token_refresh_error', err));
          reject(err);
        });
      });
    }
    /**
     * Checks whether there are tokens in the hash fragment
     * as a result of the implicit flow. These tokens are
     * parsed, validated and used to sign the user in to the
     * current client.
     *
     * @param options Optional options.
     */
    tryLoginImplicitFlow(options = null) {
      options = options || {};
      let parts;
      if (options.customHashFragment) {
        parts = this.urlHelper.getHashFragmentParams(options.customHashFragment);
      } else {
        parts = this.urlHelper.getHashFragmentParams();
      }
      this.debug('parsed url', parts);
      const state = parts['state'];
      const [nonceInState, userState] = this.parseState(state);
      this.state = userState;
      if (parts['error']) {
        this.debug('error trying to login');
        this.handleLoginError(options, parts);
        const err = new OAuthErrorEvent('token_error', {}, parts);
        this.eventsSubject.next(err);
        return Promise.reject(err);
      }
      const accessToken = parts['access_token'];
      const idToken = parts['id_token'];
      const sessionState = parts['session_state'];
      const grantedScopes = parts['scope'];
      if (!this.requestAccessToken && !this.oidc) {
        return Promise.reject('Either requestAccessToken or oidc (or both) must be true.');
      }
      if (this.requestAccessToken && !accessToken) {
        return Promise.resolve(false);
      }
      if (this.requestAccessToken && !options.disableOAuth2StateCheck && !state) {
        return Promise.resolve(false);
      }
      if (this.oidc && !idToken) {
        return Promise.resolve(false);
      }
      if (this.sessionChecksEnabled && !sessionState) {
        this.logger.warn('session checks (Session Status Change Notification) ' + 'were activated in the configuration but the id_token ' + 'does not contain a session_state claim');
      }
      if (this.requestAccessToken && !options.disableNonceCheck) {
        const success = this.validateNonce(nonceInState);
        if (!success) {
          const event = new OAuthErrorEvent('invalid_nonce_in_state', null);
          this.eventsSubject.next(event);
          return Promise.reject(event);
        }
      }
      if (this.requestAccessToken) {
        this.storeAccessTokenResponse(accessToken, null, parts['expires_in'] || this.fallbackAccessTokenExpirationTimeInSec, grantedScopes);
      }
      if (!this.oidc) {
        this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
        if (this.clearHashAfterLogin && !options.preventClearHashAfterLogin) {
          this.clearLocationHash();
        }
        this.callOnTokenReceivedIfExists(options);
        return Promise.resolve(true);
      }
      return this.processIdToken(idToken, accessToken, options.disableNonceCheck).then(result => {
        if (options.validationHandler) {
          return options.validationHandler({
            accessToken: accessToken,
            idClaims: result.idTokenClaims,
            idToken: result.idToken,
            state: state
          }).then(() => result);
        }
        return result;
      }).then(result => {
        this.storeIdToken(result);
        this.storeSessionState(sessionState);
        if (this.clearHashAfterLogin && !options.preventClearHashAfterLogin) {
          this.clearLocationHash();
        }
        this.eventsSubject.next(new OAuthSuccessEvent('token_received'));
        this.callOnTokenReceivedIfExists(options);
        this.inImplicitFlow = false;
        return true;
      }).catch(reason => {
        this.eventsSubject.next(new OAuthErrorEvent('token_validation_error', reason));
        this.logger.error('Error validating tokens');
        this.logger.error(reason);
        return Promise.reject(reason);
      });
    }
    parseState(state) {
      let nonce = state;
      let userState = '';
      if (state) {
        const idx = state.indexOf(this.config.nonceStateSeparator);
        if (idx > -1) {
          nonce = state.substr(0, idx);
          userState = state.substr(idx + this.config.nonceStateSeparator.length);
        }
      }
      return [nonce, userState];
    }
    validateNonce(nonceInState) {
      let savedNonce;
      if (this.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined') {
        savedNonce = localStorage.getItem('nonce');
      } else {
        savedNonce = this._storage.getItem('nonce');
      }
      if (savedNonce !== nonceInState) {
        const err = 'Validating access_token failed, wrong state/nonce.';
        console.error(err, savedNonce, nonceInState);
        return false;
      }
      return true;
    }
    storeIdToken(idToken) {
      this._storage.setItem('id_token', idToken.idToken);
      this._storage.setItem('id_token_claims_obj', idToken.idTokenClaimsJson);
      this._storage.setItem('id_token_expires_at', '' + idToken.idTokenExpiresAt);
      this._storage.setItem('id_token_stored_at', '' + this.dateTimeService.now());
    }
    storeSessionState(sessionState) {
      this._storage.setItem('session_state', sessionState);
    }
    getSessionState() {
      return this._storage.getItem('session_state');
    }
    handleLoginError(options, parts) {
      if (options.onLoginError) {
        options.onLoginError(parts);
      }
      if (this.clearHashAfterLogin && !options.preventClearHashAfterLogin) {
        this.clearLocationHash();
      }
    }
    getClockSkewInMsec(defaultSkewMsc = 600_000) {
      if (!this.clockSkewInSec && this.clockSkewInSec !== 0) {
        return defaultSkewMsc;
      }
      return this.clockSkewInSec * 1000;
    }
    /**
     * @ignore
     */
    processIdToken(idToken, accessToken, skipNonceCheck = false) {
      const tokenParts = idToken.split('.');
      const headerBase64 = this.padBase64(tokenParts[0]);
      const headerJson = b64DecodeUnicode(headerBase64);
      const header = JSON.parse(headerJson);
      const claimsBase64 = this.padBase64(tokenParts[1]);
      const claimsJson = b64DecodeUnicode(claimsBase64);
      const claims = JSON.parse(claimsJson);
      let savedNonce;
      if (this.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined') {
        savedNonce = localStorage.getItem('nonce');
      } else {
        savedNonce = this._storage.getItem('nonce');
      }
      if (Array.isArray(claims.aud)) {
        if (claims.aud.every(v => v !== this.clientId)) {
          const err = 'Wrong audience: ' + claims.aud.join(',');
          this.logger.warn(err);
          return Promise.reject(err);
        }
      } else {
        if (claims.aud !== this.clientId) {
          const err = 'Wrong audience: ' + claims.aud;
          this.logger.warn(err);
          return Promise.reject(err);
        }
      }
      if (!claims.sub) {
        const err = 'No sub claim in id_token';
        this.logger.warn(err);
        return Promise.reject(err);
      }
      /* For now, we only check whether the sub against
       * silentRefreshSubject when sessionChecksEnabled is on
       * We will reconsider in a later version to do this
       * in every other case too.
       */
      if (this.sessionChecksEnabled && this.silentRefreshSubject && this.silentRefreshSubject !== claims['sub']) {
        const err = 'After refreshing, we got an id_token for another user (sub). ' + `Expected sub: ${this.silentRefreshSubject}, received sub: ${claims['sub']}`;
        this.logger.warn(err);
        return Promise.reject(err);
      }
      if (!claims.iat) {
        const err = 'No iat claim in id_token';
        this.logger.warn(err);
        return Promise.reject(err);
      }
      if (!this.skipIssuerCheck && claims.iss !== this.issuer) {
        const err = 'Wrong issuer: ' + claims.iss;
        this.logger.warn(err);
        return Promise.reject(err);
      }
      if (!skipNonceCheck && claims.nonce !== savedNonce) {
        const err = 'Wrong nonce: ' + claims.nonce;
        this.logger.warn(err);
        return Promise.reject(err);
      }
      // at_hash is not applicable to authorization code flow
      // addressing https://github.com/manfredsteyer/angular-oauth2-oidc/issues/661
      // i.e. Based on spec the at_hash check is only true for implicit code flow on Ping Federate
      // https://www.pingidentity.com/developer/en/resources/openid-connect-developers-guide.html
      if (Object.prototype.hasOwnProperty.call(this, 'responseType') && (this.responseType === 'code' || this.responseType === 'id_token')) {
        this.disableAtHashCheck = true;
      }
      if (!this.disableAtHashCheck && this.requestAccessToken && !claims['at_hash']) {
        const err = 'An at_hash is needed!';
        this.logger.warn(err);
        return Promise.reject(err);
      }
      const now = this.dateTimeService.now();
      const issuedAtMSec = claims.iat * 1000;
      const expiresAtMSec = claims.exp * 1000;
      const clockSkewInMSec = this.getClockSkewInMsec(); // (this.getClockSkewInMsec() || 600) * 1000;
      if (issuedAtMSec - clockSkewInMSec >= now || expiresAtMSec + clockSkewInMSec - this.decreaseExpirationBySec <= now) {
        const err = 'Token has expired';
        console.error(err);
        console.error({
          now: now,
          issuedAtMSec: issuedAtMSec,
          expiresAtMSec: expiresAtMSec
        });
        return Promise.reject(err);
      }
      const validationParams = {
        accessToken: accessToken,
        idToken: idToken,
        jwks: this.jwks,
        idTokenClaims: claims,
        idTokenHeader: header,
        loadKeys: () => this.loadJwks()
      };
      if (this.disableAtHashCheck) {
        return this.checkSignature(validationParams).then(() => {
          const result = {
            idToken: idToken,
            idTokenClaims: claims,
            idTokenClaimsJson: claimsJson,
            idTokenHeader: header,
            idTokenHeaderJson: headerJson,
            idTokenExpiresAt: expiresAtMSec
          };
          return result;
        });
      }
      return this.checkAtHash(validationParams).then(atHashValid => {
        if (!this.disableAtHashCheck && this.requestAccessToken && !atHashValid) {
          const err = 'Wrong at_hash';
          this.logger.warn(err);
          return Promise.reject(err);
        }
        return this.checkSignature(validationParams).then(() => {
          const atHashCheckEnabled = !this.disableAtHashCheck;
          const result = {
            idToken: idToken,
            idTokenClaims: claims,
            idTokenClaimsJson: claimsJson,
            idTokenHeader: header,
            idTokenHeaderJson: headerJson,
            idTokenExpiresAt: expiresAtMSec
          };
          if (atHashCheckEnabled) {
            return this.checkAtHash(validationParams).then(atHashValid => {
              if (this.requestAccessToken && !atHashValid) {
                const err = 'Wrong at_hash';
                this.logger.warn(err);
                return Promise.reject(err);
              } else {
                return result;
              }
            });
          } else {
            return result;
          }
        });
      });
    }
    /**
     * Returns the received claims about the user.
     */
    getIdentityClaims() {
      const claims = this._storage.getItem('id_token_claims_obj');
      if (!claims) {
        return null;
      }
      return JSON.parse(claims);
    }
    /**
     * Returns the granted scopes from the server.
     */
    getGrantedScopes() {
      const scopes = this._storage.getItem('granted_scopes');
      if (!scopes) {
        return null;
      }
      return JSON.parse(scopes);
    }
    /**
     * Returns the current id_token.
     */
    getIdToken() {
      return this._storage ? this._storage.getItem('id_token') : null;
    }
    padBase64(base64data) {
      while (base64data.length % 4 !== 0) {
        base64data += '=';
      }
      return base64data;
    }
    /**
     * Returns the current access_token.
     */
    getAccessToken() {
      return this._storage ? this._storage.getItem('access_token') : null;
    }
    getRefreshToken() {
      return this._storage ? this._storage.getItem('refresh_token') : null;
    }
    /**
     * Returns the expiration date of the access_token
     * as milliseconds since 1970.
     */
    getAccessTokenExpiration() {
      if (!this._storage.getItem('expires_at')) {
        return null;
      }
      return parseInt(this._storage.getItem('expires_at'), 10);
    }
    getAccessTokenStoredAt() {
      return parseInt(this._storage.getItem('access_token_stored_at'), 10);
    }
    getIdTokenStoredAt() {
      return parseInt(this._storage.getItem('id_token_stored_at'), 10);
    }
    /**
     * Returns the expiration date of the id_token
     * as milliseconds since 1970.
     */
    getIdTokenExpiration() {
      if (!this._storage.getItem('id_token_expires_at')) {
        return null;
      }
      return parseInt(this._storage.getItem('id_token_expires_at'), 10);
    }
    /**
     * Checkes, whether there is a valid access_token.
     */
    hasValidAccessToken() {
      if (this.getAccessToken()) {
        const expiresAt = this._storage.getItem('expires_at');
        const now = this.dateTimeService.new();
        if (expiresAt && parseInt(expiresAt, 10) - this.decreaseExpirationBySec < now.getTime() - this.getClockSkewInMsec()) {
          return false;
        }
        return true;
      }
      return false;
    }
    /**
     * Checks whether there is a valid id_token.
     */
    hasValidIdToken() {
      if (this.getIdToken()) {
        const expiresAt = this._storage.getItem('id_token_expires_at');
        const now = this.dateTimeService.new();
        if (expiresAt && parseInt(expiresAt, 10) - this.decreaseExpirationBySec < now.getTime() - this.getClockSkewInMsec()) {
          return false;
        }
        return true;
      }
      return false;
    }
    /**
     * Retrieve a saved custom property of the TokenReponse object. Only if predefined in authconfig.
     */
    getCustomTokenResponseProperty(requestedProperty) {
      return this._storage && this.config.customTokenParameters && this.config.customTokenParameters.indexOf(requestedProperty) >= 0 && this._storage.getItem(requestedProperty) !== null ? JSON.parse(this._storage.getItem(requestedProperty)) : null;
    }
    /**
     * Returns the auth-header that can be used
     * to transmit the access_token to a service
     */
    authorizationHeader() {
      return 'Bearer ' + this.getAccessToken();
    }
    logOut(customParameters = {}, state = '') {
      let noRedirectToLogoutUrl = false;
      if (typeof customParameters === 'boolean') {
        noRedirectToLogoutUrl = customParameters;
        customParameters = {};
      }
      const id_token = this.getIdToken();
      this._storage.removeItem('access_token');
      this._storage.removeItem('id_token');
      this._storage.removeItem('refresh_token');
      if (this.saveNoncesInLocalStorage) {
        localStorage.removeItem('nonce');
        localStorage.removeItem('PKCE_verifier');
      } else {
        this._storage.removeItem('nonce');
        this._storage.removeItem('PKCE_verifier');
      }
      this._storage.removeItem('expires_at');
      this._storage.removeItem('id_token_claims_obj');
      this._storage.removeItem('id_token_expires_at');
      this._storage.removeItem('id_token_stored_at');
      this._storage.removeItem('access_token_stored_at');
      this._storage.removeItem('granted_scopes');
      this._storage.removeItem('session_state');
      if (this.config.customTokenParameters) {
        this.config.customTokenParameters.forEach(customParam => this._storage.removeItem(customParam));
      }
      this.silentRefreshSubject = null;
      this.eventsSubject.next(new OAuthInfoEvent('logout'));
      if (!this.logoutUrl) {
        return;
      }
      if (noRedirectToLogoutUrl) {
        return;
      }
      // if (!id_token && !this.postLogoutRedirectUri) {
      //   return;
      // }
      let logoutUrl;
      if (!this.validateUrlForHttps(this.logoutUrl)) {
        throw new Error("logoutUrl  must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
      }
      // For backward compatibility
      if (this.logoutUrl.indexOf('{{') > -1) {
        logoutUrl = this.logoutUrl.replace(/\{\{id_token\}\}/, encodeURIComponent(id_token)).replace(/\{\{client_id\}\}/, encodeURIComponent(this.clientId));
      } else {
        let params = new HttpParams({
          encoder: new WebHttpUrlEncodingCodec()
        });
        if (id_token) {
          params = params.set('id_token_hint', id_token);
        }
        const postLogoutUrl = this.postLogoutRedirectUri || this.redirectUriAsPostLogoutRedirectUriFallback && this.redirectUri || '';
        if (postLogoutUrl) {
          params = params.set('post_logout_redirect_uri', postLogoutUrl);
          if (state) {
            params = params.set('state', state);
          }
        }
        for (const key in customParameters) {
          params = params.set(key, customParameters[key]);
        }
        logoutUrl = this.logoutUrl + (this.logoutUrl.indexOf('?') > -1 ? '&' : '?') + params.toString();
      }
      this.config.openUri(logoutUrl);
    }
    /**
     * @ignore
     */
    createAndSaveNonce() {
      const that = this; // eslint-disable-line @typescript-eslint/no-this-alias
      return this.createNonce().then(function (nonce) {
        // Use localStorage for nonce if possible
        // localStorage is the only storage who survives a
        // redirect in ALL browsers (also IE)
        // Otherwiese we'd force teams who have to support
        // IE into using localStorage for everything
        if (that.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined') {
          localStorage.setItem('nonce', nonce);
        } else {
          that._storage.setItem('nonce', nonce);
        }
        return nonce;
      });
    }
    /**
     * @ignore
     */
    ngOnDestroy() {
      this.clearAccessTokenTimer();
      this.clearIdTokenTimer();
      this.removeSilentRefreshEventListener();
      const silentRefreshFrame = this.document.getElementById(this.silentRefreshIFrameName);
      if (silentRefreshFrame) {
        silentRefreshFrame.remove();
      }
      this.stopSessionCheckTimer();
      this.removeSessionCheckEventListener();
      const sessionCheckFrame = this.document.getElementById(this.sessionCheckIFrameName);
      if (sessionCheckFrame) {
        sessionCheckFrame.remove();
      }
    }
    createNonce() {
      return new Promise(resolve => {
        if (this.rngUrl) {
          throw new Error('createNonce with rng-web-api has not been implemented so far');
        }
        /*
         * This alphabet is from:
         * https://tools.ietf.org/html/rfc7636#section-4.1
         *
         * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
         */
        const unreserved = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        let size = 45;
        let id = '';
        const crypto = typeof self === 'undefined' ? null : self.crypto || self['msCrypto'];
        if (crypto) {
          let bytes = new Uint8Array(size);
          crypto.getRandomValues(bytes);
          // Needed for IE
          if (!bytes.map) {
            bytes.map = Array.prototype.map;
          }
          bytes = bytes.map(x => unreserved.charCodeAt(x % unreserved.length));
          id = String.fromCharCode.apply(null, bytes);
        } else {
          while (0 < size--) {
            id += unreserved[Math.random() * unreserved.length | 0];
          }
        }
        resolve(base64UrlEncode(id));
      });
    }
    async checkAtHash(params) {
      if (!this.tokenValidationHandler) {
        this.logger.warn('No tokenValidationHandler configured. Cannot check at_hash.');
        return true;
      }
      return this.tokenValidationHandler.validateAtHash(params);
    }
    checkSignature(params) {
      if (!this.tokenValidationHandler) {
        this.logger.warn('No tokenValidationHandler configured. Cannot check signature.');
        return Promise.resolve(null);
      }
      return this.tokenValidationHandler.validateSignature(params);
    }
    /**
     * Start the implicit flow or the code flow,
     * depending on your configuration.
     */
    initLoginFlow(additionalState = '', params = {}) {
      if (this.responseType === 'code') {
        return this.initCodeFlow(additionalState, params);
      } else {
        return this.initImplicitFlow(additionalState, params);
      }
    }
    /**
     * Starts the authorization code flow and redirects to user to
     * the auth servers login url.
     */
    initCodeFlow(additionalState = '', params = {}) {
      if (this.loginUrl !== '') {
        this.initCodeFlowInternal(additionalState, params);
      } else {
        this.events.pipe(filter(e => e.type === 'discovery_document_loaded')).subscribe(() => this.initCodeFlowInternal(additionalState, params));
      }
    }
    initCodeFlowInternal(additionalState = '', params = {}) {
      if (!this.validateUrlForHttps(this.loginUrl)) {
        throw new Error("loginUrl  must use HTTPS (with TLS), or config value for property 'requireHttps' must be set to 'false' and allow HTTP (without TLS).");
      }
      let addParams = {};
      let loginHint = null;
      if (typeof params === 'string') {
        loginHint = params;
      } else if (typeof params === 'object') {
        addParams = params;
      }
      this.createLoginUrl(additionalState, loginHint, null, false, addParams).then(this.config.openUri).catch(error => {
        console.error('Error in initAuthorizationCodeFlow');
        console.error(error);
      });
    }
    async createChallangeVerifierPairForPKCE() {
      if (!this.crypto) {
        throw new Error('PKCE support for code flow needs a CryptoHander. Did you import the OAuthModule using forRoot() ?');
      }
      const verifier = await this.createNonce();
      const challengeRaw = await this.crypto.calcHash(verifier, 'sha-256');
      const challenge = base64UrlEncode(challengeRaw);
      return [challenge, verifier];
    }
    extractRecognizedCustomParameters(tokenResponse) {
      const foundParameters = new Map();
      if (!this.config.customTokenParameters) {
        return foundParameters;
      }
      this.config.customTokenParameters.forEach(recognizedParameter => {
        if (tokenResponse[recognizedParameter]) {
          foundParameters.set(recognizedParameter, JSON.stringify(tokenResponse[recognizedParameter]));
        }
      });
      return foundParameters;
    }
    /**
     * Revokes the auth token to secure the vulnarability
     * of the token issued allowing the authorization server to clean
     * up any security credentials associated with the authorization
     */
    revokeTokenAndLogout(customParameters = {}, ignoreCorsIssues = false) {
      const revokeEndpoint = this.revocationEndpoint;
      const accessToken = this.getAccessToken();
      const refreshToken = this.getRefreshToken();
      if (!accessToken) {
        return Promise.resolve();
      }
      let params = new HttpParams({
        encoder: new WebHttpUrlEncodingCodec()
      });
      let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      if (this.useHttpBasicAuth) {
        const header = btoa(`${this.clientId}:${this.dummyClientSecret}`);
        headers = headers.set('Authorization', 'Basic ' + header);
      }
      if (!this.useHttpBasicAuth) {
        params = params.set('client_id', this.clientId);
      }
      if (!this.useHttpBasicAuth && this.dummyClientSecret) {
        params = params.set('client_secret', this.dummyClientSecret);
      }
      if (this.customQueryParams) {
        for (const key of Object.getOwnPropertyNames(this.customQueryParams)) {
          params = params.set(key, this.customQueryParams[key]);
        }
      }
      return new Promise((resolve, reject) => {
        let revokeAccessToken;
        let revokeRefreshToken;
        if (accessToken) {
          const revokationParams = params.set('token', accessToken).set('token_type_hint', 'access_token');
          revokeAccessToken = this.http.post(revokeEndpoint, revokationParams, {
            headers
          });
        } else {
          revokeAccessToken = of(null);
        }
        if (refreshToken) {
          const revokationParams = params.set('token', refreshToken).set('token_type_hint', 'refresh_token');
          revokeRefreshToken = this.http.post(revokeEndpoint, revokationParams, {
            headers
          });
        } else {
          revokeRefreshToken = of(null);
        }
        if (ignoreCorsIssues) {
          revokeAccessToken = revokeAccessToken.pipe(catchError(err => {
            if (err.status === 0) {
              return of(null);
            }
            return throwError(err);
          }));
          revokeRefreshToken = revokeRefreshToken.pipe(catchError(err => {
            if (err.status === 0) {
              return of(null);
            }
            return throwError(err);
          }));
        }
        combineLatest([revokeAccessToken, revokeRefreshToken]).subscribe(res => {
          this.logOut(customParameters);
          resolve(res);
          this.logger.info('Token successfully revoked');
        }, err => {
          this.logger.error('Error revoking token', err);
          this.eventsSubject.next(new OAuthErrorEvent('token_revoke_error', err));
          reject(err);
        });
      });
    }
    /**
     * Clear location.hash if it's present
     */
    clearLocationHash() {
      // Checking for empty hash is necessary for Firefox
      // as setting an empty hash to an empty string adds # to the URL
      if (location.hash != '') {
        location.hash = '';
      }
    }
    static {
      this.ɵfac = function OAuthService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OAuthService)(i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(OAuthStorage, 8), i0.ɵɵinject(ValidationHandler, 8), i0.ɵɵinject(AuthConfig, 8), i0.ɵɵinject(UrlHelperService), i0.ɵɵinject(OAuthLogger), i0.ɵɵinject(HashHandler, 8), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(DateTimeProvider));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OAuthService,
        factory: OAuthService.ɵfac
      });
    }
  }
  return OAuthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class OAuthResourceServerErrorHandler {}
class OAuthNoopResourceServerErrorHandler {
  handleError(err) {
    return throwError(err);
  }
}
let DefaultOAuthInterceptor = /*#__PURE__*/(() => {
  class DefaultOAuthInterceptor {
    constructor(oAuthService, errorHandler, moduleConfig) {
      this.oAuthService = oAuthService;
      this.errorHandler = errorHandler;
      this.moduleConfig = moduleConfig;
    }
    checkUrl(url) {
      if (this.moduleConfig.resourceServer.customUrlValidation) {
        return this.moduleConfig.resourceServer.customUrlValidation(url);
      }
      if (this.moduleConfig.resourceServer.allowedUrls) {
        return !!this.moduleConfig.resourceServer.allowedUrls.find(u => url.toLowerCase().startsWith(u.toLowerCase()));
      }
      return true;
    }
    intercept(req, next) {
      const url = req.url.toLowerCase();
      if (!this.moduleConfig || !this.moduleConfig.resourceServer || !this.checkUrl(url)) {
        return next.handle(req);
      }
      const sendAccessToken = this.moduleConfig.resourceServer.sendAccessToken;
      if (!sendAccessToken) {
        return next.handle(req).pipe(catchError(err => this.errorHandler.handleError(err)));
      }
      return merge(of(this.oAuthService.getAccessToken()).pipe(filter(token => !!token)), this.oAuthService.events.pipe(filter(e => e.type === 'token_received'), timeout(this.oAuthService.waitForTokenInMsec || 0), catchError(() => of(null)),
      // timeout is not an error
      map(() => this.oAuthService.getAccessToken()))).pipe(take(1), mergeMap(token => {
        if (token) {
          const header = 'Bearer ' + token;
          const headers = req.headers.set('Authorization', header);
          req = req.clone({
            headers
          });
        }
        return next.handle(req).pipe(catchError(err => this.errorHandler.handleError(err)));
      }));
    }
    static {
      this.ɵfac = function DefaultOAuthInterceptor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DefaultOAuthInterceptor)(i0.ɵɵinject(OAuthService), i0.ɵɵinject(OAuthResourceServerErrorHandler), i0.ɵɵinject(OAuthModuleConfig, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DefaultOAuthInterceptor,
        factory: DefaultOAuthInterceptor.ɵfac
      });
    }
  }
  return DefaultOAuthInterceptor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function createDefaultLogger() {
  return console;
}
function createDefaultStorage() {
  return typeof sessionStorage !== 'undefined' ? sessionStorage : new MemoryStorage();
}
function provideOAuthClient(config = null, validationHandlerClass = NullValidationHandler) {
  return makeEnvironmentProviders([OAuthService, UrlHelperService, {
    provide: OAuthLogger,
    useFactory: createDefaultLogger
  }, {
    provide: OAuthStorage,
    useFactory: createDefaultStorage
  }, {
    provide: ValidationHandler,
    useClass: validationHandlerClass
  }, {
    provide: HashHandler,
    useClass: DefaultHashHandler
  }, {
    provide: OAuthResourceServerErrorHandler,
    useClass: OAuthNoopResourceServerErrorHandler
  }, {
    provide: OAuthModuleConfig,
    useValue: config
  }, {
    provide: HTTP_INTERCEPTORS,
    useClass: DefaultOAuthInterceptor,
    multi: true
  }, {
    provide: DateTimeProvider,
    useClass: SystemDateTimeProvider
  }]);
}
let OAuthModule = /*#__PURE__*/(() => {
  class OAuthModule {
    static forRoot(config = null, validationHandlerClass = NullValidationHandler) {
      return {
        ngModule: OAuthModule,
        providers: [provideOAuthClient(config, validationHandlerClass)]
      };
    }
    static {
      this.ɵfac = function OAuthModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OAuthModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: OAuthModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return OAuthModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const err = `PLEASE READ THIS CAREFULLY:

Beginning with angular-oauth2-oidc version 9, the JwksValidationHandler
has been moved to an library of its own. If you need it for implementing
OAuth2/OIDC **implicit flow**, please install it using npm:

  npm i angular-oauth2-oidc-jwks --save

After that, you can import it into your application:

  import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

Please note, that this dependency is not needed for the **code flow**,
which is nowadays the **recommented** one for single page applications.
This also results in smaller bundle sizes.
`;
/**
 * This is just a dummy of the JwksValidationHandler
 * telling the users that the real one has been moved
 * to an library of its own, namely angular-oauth2-oidc-utils
 */
class JwksValidationHandler extends NullValidationHandler {
  constructor() {
    super();
    console.error(err);
  }
}
const AUTH_CONFIG = new InjectionToken('AUTH_CONFIG');

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AbstractValidationHandler, AuthConfig, DateTimeProvider, DefaultHashHandler, DefaultOAuthInterceptor, HashHandler, JwksValidationHandler, LoginOptions, MemoryStorage, NullValidationHandler, OAuthErrorEvent, OAuthEvent, OAuthInfoEvent, OAuthLogger, OAuthModule, OAuthModuleConfig, OAuthNoopResourceServerErrorHandler, OAuthResourceServerConfig, OAuthResourceServerErrorHandler, OAuthService, OAuthStorage, OAuthSuccessEvent, ReceivedTokens, SystemDateTimeProvider, UrlHelperService, ValidationHandler, provideOAuthClient };
